import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui';
import { Button } from '@/components/ui';
import { ActionsTable } from '@/components/dashboard/ActionsTable';
import { StrategicOverview } from '@/components/dashboard/StrategicOverview';
import { InternationalSection } from '@/components/dashboard/InternationalSection';
import { LocalSection } from '@/components/dashboard/LocalSection';
import { useRestaurants } from '@/hooks/useRestaurants';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import {
  Building2,
  Sparkles,
  Loader2,
  AlertCircle
} from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const { data: restaurants, isLoading, error } = useRestaurants();

  // État de chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-stratos-gold animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Gestion des erreurs
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-red-800 font-bold text-xl mb-2 text-center">
            Erreur de chargement
          </h2>
          <p className="text-red-700 text-center">
            {error instanceof Error ? error.message : 'Erreur inconnue'}
          </p>
        </div>
      </div>
    );
  }

  // Aucun restaurant trouvé
  if (!restaurants || restaurants.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Aucun restaurant
          </h2>
          <p className="text-gray-600">
            Aucun restaurant n'a été trouvé dans la base de données.
          </p>
        </div>
      </div>
    );
  }

  // Sélectionner le premier restaurant par défaut si le slug actuel n'existe pas
  const currentRestaurant = restaurants.find(r => r.slug === selectedRestaurant);
  if (!currentRestaurant && restaurants.length > 0) {
    setSelectedRestaurant(restaurants[0].slug);
  }

  return (
    <div className="space-y-6 sm:space-y-8 lg:space-y-12">
      {/* Badge "Database Connected" */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-end"
      >
        <Badge variant="success" className="bg-green-100 text-green-800">
          <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
          Database Connected
        </Badge>
      </motion.div>

      {/* En-tête avec sélecteur d'établissement */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-stratos-gold to-gray-900 bg-clip-text text-transparent flex items-center gap-2 sm:gap-3">
            <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-stratos-gold" />
            Dashboard Stratégique
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-1 sm:mt-2">
            Analyse complète et plan d'action pour maximiser vos réservations
          </p>
          <p className="text-xs text-gray-500 mt-1">
            🚀 Données en temps réel depuis Supabase
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {restaurants.map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.slug ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.slug)}
              className={
                selectedRestaurant === restaurant.slug
                  ? "bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900 border-0 shadow-lg shadow-stratos-gold/30"
                  : "border-gray-300 hover:border-stratos-gold hover:text-stratos-gold"
              }
              size="lg"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {restaurant.name}
              {restaurant.status === 'active' && (
                <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Info Card - Données Supabase */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <div className="bg-blue-600 rounded-lg p-2">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">
              Mode Base de Données Activé
            </h3>
            <p className="text-sm text-blue-700">
              Les données affichées proviennent de Supabase en temps réel. 
              {currentRestaurant && (
                <span className="font-medium">
                  {' '}Restaurant sélectionné : <strong>{currentRestaurant.name}</strong> ({currentRestaurant.pack || 'Pas de pack'})
                </span>
              )}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Tableau récapitulatif des actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <ActionsTable restaurantId={selectedRestaurant} />
      </motion.div>

      {/* Divider */}
      <div className="relative py-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="px-4 text-sm text-gray-500 bg-gray-50">
            Analyse Stratégique Détaillée
          </span>
        </div>
      </div>

      {/* Analyse Stratégique */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <StrategicOverview restaurantId={selectedRestaurant} />
      </motion.div>

      {/* Divider */}
      <div className="relative py-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="px-4 text-sm text-gray-500 bg-gray-50">
            Métriques Détaillées
          </span>
        </div>
      </div>

      {/* Section SEO International */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <InternationalSection restaurantId={selectedRestaurant} />
      </motion.div>

      {/* Section SEO Local / GEO */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <LocalSection restaurantId={selectedRestaurant} />
      </motion.div>
    </div>
  );
};

